import { createFileRoute } from "@tanstack/react-router";
import { LocationPageTemplate, type LocationVariant } from "@/components/LocationPageTemplate";
import { getLocationCity } from "@/data/location-cities";
import { canonical } from "@/lib/seo";

const PATH = "/voip-montclair-ca";
const TITLE = "VoIP Phone Systems Montclair CA | (909) 255-9112";
const DESCRIPTION =
  "Looking for VoIP in Montclair, CA? SimpleTech Helper installs & supports business VoIP phone systems. 50+ providers, zero downtime. Call (909) 255-9112.";

export const Route = createFileRoute("/voip-montclair-ca")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "VoIP Montclair CA, business phone systems Montclair, VoIP installation Inland Empire, RingCentral Zoom Phone Nextiva Montclair, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical(PATH) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Montclair" },
      { name: "geo.position", content: "34.0775;-117.6897" },
      { name: "ICBM", content: "34.0775, -117.6897" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          telephone: "(909) 255-9112",
          url: "https://simpletechhelper.com",
          areaServed: "Montclair CA, Inland Empire",
          serviceType: "VoIP Phone Systems",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: VoipMontclairPage,
});

const FAQS = [
  {
    q: "Can I keep my existing Montclair business phone number?",
    a: "Yes. We handle full number porting from your current provider to your new VoIP platform with zero downtime — your Montclair customers keep calling the same number they always have.",
  },
  {
    q: "How much can a Montclair business save by switching to VoIP?",
    a: "Most Montclair clients save 30–60% versus their current landline or first-generation VoIP plan. We'll put together a written side-by-side comparison quote across our 50+ provider partners.",
  },
  {
    q: "Is VoIP reliable enough for a busy Montclair office?",
    a: "Yes — when it's set up correctly. We tune your network with QoS, verify bandwidth, and recommend cellular or secondary-internet failover so your phones stay up even if your primary connection blips.",
  },
  {
    q: "Do you install physical phones or just apps?",
    a: "Both. Desk handsets, mobile softphone apps, web dialers, headsets — whatever your Montclair team actually uses day to day. We'll recommend the right mix based on roles and call volume.",
  },
  {
    q: "Is VoIP HIPAA-compliant for Montclair medical offices?",
    a: "Yes, when configured correctly. We set up call recording controls, encryption, and Business Associate Agreements (BAAs) with HIPAA-eligible providers for Montclair medical, dental, and behavioral health offices.",
  },
  {
    q: "How long does a VoIP installation take in Montclair?",
    a: "Most Montclair installations complete in one business day. Larger multi-location or complex call-flow setups typically run 2–3 days, scheduled around your business hours so you don't lose calls.",
  },
  {
    q: "Can you integrate VoIP with our CRM or Microsoft 365?",
    a: "Yes. Click-to-dial, screen pops, and call logging into Salesforce, HubSpot, Microsoft 365, Google Workspace, and most major CRMs used by Montclair small businesses.",
  },
];

const INTRO = (
  <>
    <p>
      Small businesses in Montclair, CA are still paying landline rates for phone service that
      drops calls, can't forward to mobile, and has no voicemail-to-email.{" "}
      <strong className="text-[#0b0a3d]">SimpleTech Helper</strong> delivers{" "}
      <strong className="text-[#0b0a3d]">VoIP Montclair CA</strong> businesses actually use —
      systems that work at the office, at home, and on the road, from a local team based at{" "}
      <strong className="text-[#0b0a3d]">8880 N Benson Ave #121, Montclair, CA 91763</strong>.
    </p>
    <p>
      We assess your call volume and team size, compare our 50+ provider partners — RingCentral,
      Zoom Phone, Spectrum Business, Nextiva, 8x8, Vonage, Ooma, GoTo Connect, Dialpad,
      Intermedia, Mitel, and more — side-by-side, and install everything with zero downtime.
      Most Montclair clients cut their phone bill by 30–60%.
    </p>
    <p>
      Ready for a free VoIP quote? Call{" "}
      <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
        {"\n"}(909) 255-9112
      </a>
      .
    </p>
  </>
);

const VARIANT: LocationVariant = {
  serviceLabel: "VoIP Phone Systems",
  serviceLabelShort: "VoIP",
  parentHref: "/services/voip",
  parentLabel: "VoIP",
  learnMoreText: "EXPLORE OUR VoIP SERVICES",
  nearMeEyebrow: "VoIP Near Me",
  whyTitle: "Why Montclair Businesses Choose SimpleTech for VoIP",
  expertHeroTitle:
    "Business VoIP Phone Systems in Montclair, CA — Cut Your Bill, Ditch the Dropped Calls",
  expertNearMeTitle: "Trusted VoIP Phone Systems in Montclair, CA",
  introStrong: "VoIP Montclair CA",
  introBody: INTRO,
  faqHeading: (
    <>Frequently Asked<br />Questions About<br />VoIP in Montclair</>
  ),
  ctaTitle: "Ready to Cut Your Phone Bill in Montclair?",
  blogEyebrow: "From the Blog",
  blogTitle: "VoIP & Phone System Tips for Montclair Businesses",
  faqs: FAQS,
};

function VoipMontclairPage() {
  const city = getLocationCity("montclair")!;
  return <LocationPageTemplate city={city} variant={VARIANT} />;
}
